// src/services/authService.js
import { toast } from 'react-toastify';

class AuthService {
  // Core auth base (login / refresh / logout / me)
  static get authBase() {
    const base = window.APP_CONFIG?.coreAuthBase || 'http://localhost:8080/core';
    return `${base}/auth`;
  }

  // Edefter business API base (unchanged)
  static get apiBase() {
    return window.APP_CONFIG?.apiUrl || 'https://api.dpg.net.tr';
  }

  static get apiTimeout() {
    return window.APP_CONFIG?.apiTimeout || 30000;
  }

  // Login process - Fidost Core auth
  static login = async (credentials) => {
    try {
      const payload = {
        email: credentials.email,
        password: credentials.password,
        rememberMe: credentials.rememberMe || false
      };

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.apiTimeout);

      const response = await fetch(`${this.authBase}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.accessToken) {
        if (data.mfaRequired) {
          return { success: false, mfaRequired: true };
        }

        // Normalise: Core returns roles[] array; keep role (first entry) for
        // backward-compat with existing UI code that reads user.role.
        const roles = data.user?.roles || [];
        return {
          success: true,
          data: {
            token: data.accessToken,
            refreshToken: data.refreshToken,
            expiresInSec: data.expiresInSec,
            user: {
              id: data.user?.id,
              email: data.user?.email,
              name: data.user?.name,
              surname: data.user?.surname,
              role: roles[0] || '',
              roles
            }
          }
        };
      } else {
        return {
          success: false,
          message: data.message || data.error || 'Email address or password is incorrect.'
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: 'Server error. Please try again later.'
      };
    }
  };

  // Registration process - REAL BACKEND CALL
  static register = async (formData) => {
    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        surname: formData.surname,
        phone: formData.phone || undefined,
        title: formData.title || undefined,
      };

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.apiTimeout);

      const response = await fetch(`${this.authBase}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await response.json().catch(() => ({}));

      if (response.ok || response.status === 201) {
        return { success: true, data };
      }

      const message = data.message || data.Message || 'Registration failed.';
      return { success: false, message };
    } catch (error) {
      console.error('Register error:', error);
      return { success: false, message: 'Server error. Please try again later.' };
    }
  };

  // Logout process - Core auth (fire-and-forget)
  static logoutFromServer = async () => {
    try {
      const token = this.getAuthToken();
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) return true;

      fetch(`${this.authBase}/logout`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      }).catch(() => {
        // Ignore errors - we're logging out anyway
      });

      return true; // Return immediately
    } catch (error) {
      // Ignore errors - we're logging out anyway
      return true;
    }
  };

  // Current User Info - Core auth
  static getCurrentUserFromServer = async () => {
    try {
      const token = this.getAuthToken();
      if (!token) return null;

      const response = await fetch(`${this.authBase}/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }

      if (response.status === 401) {
        // Token invalid, logout
        this.logout(false);
        return null;
      }

      return null;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  };

  // Save token and user data (also stores refreshToken)
  static setAuthData = (token, userData, rememberMe = false, refreshToken = null) => {
    const storage = rememberMe ? localStorage : sessionStorage;
    
    // Clear previous auth data from both storages
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('userData');
    sessionStorage.removeItem('tokenExpiry');
    sessionStorage.removeItem('rememberMe');
    
    // Store new auth data in appropriate storage
    storage.setItem('authToken', token);
    if (refreshToken) storage.setItem('refreshToken', refreshToken);
    storage.setItem('userData', JSON.stringify(userData));
    
    // Token expiry time - rememberMe: 30 days, normal: 8 hours
    const expiryTime = Date.now() + (rememberMe ? 30 * 24 * 60 * 60 * 1000 : 8 * 60 * 60 * 1000);
    storage.setItem('tokenExpiry', expiryTime.toString());
    storage.setItem('rememberMe', rememberMe.toString());
  };

  // Token check
  static getAuthToken = () => {
    // Check localStorage first, then sessionStorage
    let token = localStorage.getItem('authToken');
    let expiry = localStorage.getItem('tokenExpiry');
    let storage = localStorage;

    if (!token) {
      token = sessionStorage.getItem('authToken');
      expiry = sessionStorage.getItem('tokenExpiry');
      storage = sessionStorage;
    }

    // Token expiry check
    if (token && expiry) {
      const now = Date.now();
      if (now > parseInt(expiry)) {
        this.logout();
        return null;
      }
    }

    return token;
  };

  // Refresh token retrieval
  static getRefreshToken = () => {
    return localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken') || null;
  };

  // Get user data
  static getCurrentUser = () => {
    try {
      let userData = localStorage.getItem('userData');
      let storage = 'localStorage';
      
      if (!userData) {
        userData = sessionStorage.getItem('userData');
        storage = 'sessionStorage';
      }
      
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  };

  // Check authentication status
  static isAuthenticated = () => {
    const token = this.getAuthToken();
    const user = this.getCurrentUser();
    return !!(token && user);
  };

  // Logout process
  static logout = (showToast = true) => {
    // Clear all auth data first (to prevent any race conditions)
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('userData');
    sessionStorage.removeItem('tokenExpiry');
    sessionStorage.removeItem('rememberMe');
    
    // Dispatch logout event immediately
    window.dispatchEvent(new CustomEvent('userLogout'));
    
    // Send logout request to backend in background (fire-and-forget)
    this.logoutFromServer().catch(() => {});
    
    // Redirect to tahsis (auth owner) after logout — avoid circular import with ssoHandoff
    const configured = window.APP_CONFIG?.tahsisAppUrl;
    const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const tahsisBase = String(configured || (isLocal ? 'http://localhost:5173' : 'https://tahsis.fidost.com')).replace(/\/+$/, '');
    window.location.replace(`${tahsisBase}/`);
  };

  // Session timeout check
  static checkSessionTimeout = () => {
    const expiry = localStorage.getItem('tokenExpiry') || sessionStorage.getItem('tokenExpiry');
    
    if (expiry) {
      const now = Date.now();
      const timeLeft = parseInt(expiry) - now;
      
      // Warning 5 minutes before
      if (timeLeft > 0 && timeLeft < 5 * 60 * 1000) {
        if (window.showToast) {
          window.showToast('Your session will expire soon', 'warning');
        }
      }
      
      // Session expired
      if (timeLeft <= 0) {
        if (window.showToast) {
          window.showToast('Your session has expired. Please sign in again.', 'error');
        }
        this.logout(false);
      }
    }
  };

  // Refresh access token using Core's /auth/refresh endpoint.
  // Returns true on success, false on failure.
  static refresh = async () => {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) return false;

      const rememberMe = (localStorage.getItem('rememberMe') || sessionStorage.getItem('rememberMe')) === 'true';

      const response = await fetch(`${this.authBase}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken, rememberMe })
      });

      if (!response.ok) return false;

      const data = await response.json();
      if (!data.accessToken) return false;

      const storage = localStorage.getItem('refreshToken') ? localStorage : sessionStorage;
      storage.setItem('authToken', data.accessToken);
      if (data.refreshToken) storage.setItem('refreshToken', data.refreshToken);

      const expiryTime = Date.now() + (rememberMe ? 30 * 24 * 60 * 60 * 1000 : 8 * 60 * 60 * 1000);
      storage.setItem('tokenExpiry', expiryTime.toString());

      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  };

  // Legacy alias (kept for any existing callers)
  static refreshToken = () => this.refresh();

  // Authorized headers for API calls
  static getAuthHeaders = () => {
    const token = this.getAuthToken();
    return token ? {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    } : {
      'Content-Type': 'application/json'
    };
  };

  static _refreshPromise = null;

  // Central authenticated fetch wrapper for Core-protected sibling APIs.
  // On 401, refreshes the Core access token once and retries the original call.
  static authenticatedFetch = async (url, options = {}) => {
    const buildHeaders = () => {
      const headers = new Headers(options.headers || {});
      const token = this.getAuthToken();
      if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    };

    const doFetch = () => fetch(url, { ...options, headers: buildHeaders() });

    try {
      let response = await doFetch();

      if (response.status === 401) {
        if (!this._refreshPromise) {
          this._refreshPromise = this.refresh().finally(() => {
            this._refreshPromise = null;
          });
        }

        const refreshed = await this._refreshPromise;
        if (!refreshed) {
          this.logout(true);
          return response;
        }

        response = await doFetch();
        if (response.status === 401) {
          this.logout(true);
          return response;
        }
      }

      return response;
    } catch (error) {
      console.error('Authenticated fetch error:', error);
      throw error;
    }
  };

  // Generic API call method
  static apiCall = async (endpoint, options = {}) => {
    try {
      const url = `${this.apiBase}${endpoint}`;
      const headers = options.requireAuth !== false ? this.getAuthHeaders() : { 'Content-Type': 'application/json' };
      
      const config = {
        headers,
        ...options,
      };

      const response = await fetch(url, config);
      
      if (response.status === 401 && options.requireAuth !== false) {
        // Unauthorized — try refresh then retry once
        const refreshed = await this.refresh();
        if (!refreshed) {
          this.logout(true);
          return null;
        }
        const retryResponse = await fetch(url, { ...config, headers: this.getAuthHeaders() });
        if (retryResponse.status === 401) {
          this.logout(true);
          return null;
        }
        const retryData = await retryResponse.json().catch(() => ({}));
        return { response: retryResponse, data: retryData };
      }

      const data = await response.json().catch(() => ({}));
      return { response, data };
    } catch (error) {
      console.error('API call error:', error);
      return null;
    }
  };

  // Role check — supports both string role and roles[]
  static hasRole = (requiredRole) => {
    const user = this.getCurrentUser();
    if (!user) return false;
    if (Array.isArray(user.roles)) return user.roles.includes(requiredRole);
    return user.role === requiredRole;
  };

  // Permission check
  static hasPermission = (permission) => {
    const user = this.getCurrentUser();
    if (!user) return false;
    
    // Role-based permissions
    const rolePermissions = {
      'Admin': ['user.create', 'user.read', 'user.update', 'user.delete', 'limit.create', 'limit.read', 'limit.update'],
      'Buyer': ['credit.create', 'credit.read', 'profile.read', 'profile.update'],
      'Seller': ['guarantee.read', 'guarantee.approve', 'guarantee.reject', 'profile.read', 'profile.update']
    };
    
    return rolePermissions[user.role]?.includes(permission) || false;
  };

  // Health check
  static checkApiHealth = async () => {
    try {
      const response = await fetch(`${this.apiBase}/health`);
      return response.ok;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  };

  // --- Core auth helpers (stubs — endpoints to be added in Core later) ---

  static forgotPassword = async (_email) => {
    return { success: false, message: 'Forgot-password endpoint not yet available.' };
  };

  static resetPassword = async (_token, _newPassword) => {
    return { success: false, message: 'Reset-password endpoint not yet available.' };
  };

  static enrollMfa = async () => {
    return { success: false, message: 'MFA enrollment not yet available.' };
  };

  static verifyMfa = async (_code) => {
    return { success: false, message: 'MFA verification not yet available.' };
  };
}

export default AuthService;
